import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Jira-like Kanban Board (TypeScript + React + Tailwind) — Single File
 *
 * Notes
 *  - Keep this file as .tsx
 *  - No external libs used (only React + Tailwind)
 */

// ---------- Types ----------
export type Status = "Backlog" | "To Do" | "In Progress" | "Review" | "Done";
export type Priority = "High" | "Medium" | "Low";

export interface Comment {
  id: string;
  text: string;
  author: string;
  timestamp: string;
  image?: string; // base64 or URL
}

export interface Sprint {
  id: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
  status: "Planning" | "Active" | "Completed";
}

export interface Task {
  id: string;
  title: string;
  description: string;
  status: Status;
  priority: Priority;
  assignee: string; // person name
  tags: string[];
  dueDate: string | null; // ISO date string
  points: number;
  comments: Comment[];
  sprintId: string | null;
}

const STATUSES: Status[] = [
  "Backlog",
  "To Do",
  "In Progress",
  "Review",
  "Done",
];

const PRIORITY_COLORS: Record<Priority, string> = {
  High: "bg-red-100 text-red-700 border-red-200",
  Medium: "bg-amber-100 text-amber-700 border-amber-200",
  Low: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const STORAGE_KEY = "jira_like_board_tasks_v1";
const SPRINTS_STORAGE_KEY = "jira_like_board_sprints_v1";

// ---------- Sample Data ----------
const SAMPLE_TASKS: Task[] = [
  {
    id: "T-1001",
    title: "Auth: Add refresh token rotation",
    description:
      "Implement rotating refresh tokens, revoke on reuse, add server-side session store.",
    status: "Backlog",
    priority: "High",
    assignee: "Amit Shah",
    tags: ["auth", "security"],
    dueDate: null,
    points: 5,
    comments: [],
    sprintId: null,
  },
  {
    id: "T-1002",
    title: "Board UI: Draggable cards",
    description: "Support drag & drop across columns with smooth hints.",
    status: "To Do",
    priority: "Medium",
    assignee: "Neha Verma",
    tags: ["ui", "ux"],
    dueDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 3).toISOString(),
    points: 3,
    comments: [],
    sprintId: null,
  },
  {
    id: "T-1003",
    title: "Payments: Retry idempotency",
    description: "Add idempotent keys for payment intents and retries.",
    status: "In Progress",
    priority: "High",
    assignee: "Rohit Kumar",
    tags: ["payments", "backend"],
    dueDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 1).toISOString(),
    points: 8,
    comments: [],
    sprintId: null,
  },
  {
    id: "T-1004",
    title: "Docs: Contributing guide",
    description: "Write a concise contributor guide and PR checklist.",
    status: "Review",
    priority: "Low",
    assignee: "Asha Iyer",
    tags: ["docs"],
    dueDate: null,
    points: 2,
    comments: [],
    sprintId: null,
  },
  {
    id: "T-1005",
    title: "CI: Cache Playwright browsers",
    description: "Speed up pipeline by caching browser binaries per version.",
    status: "Done",
    priority: "Medium",
    assignee: "Neha Verma",
    tags: ["ci", "devops"],
    dueDate: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
    points: 3,
    comments: [],
    sprintId: null,
  },
  {
    id: "T-1006",
    title: "Mobile: Crash on AVD cold boot",
    description: "Investigate emulator crash and collect logs.",
    status: "Backlog",
    priority: "Medium",
    assignee: "Rohit Kumar",
    tags: ["android", "bug"],
    dueDate: null,
    points: 2,
    comments: [],
    sprintId: null,
  },
  {
    id: "T-1007",
    title: "API: Rate limit 429 handling",
    description: "Exponential backoff and jitter for 429 responses.",
    status: "To Do",
    priority: "Low",
    assignee: "Amit Shah",
    tags: ["api", "resilience"],
    dueDate: null,
    points: 3,
    comments: [],
    sprintId: null,
  },
  {
    id: "T-1008",
    title: "Design: Portfolio hero revamp",
    description: "Create modern gradient hero with animated accents.",
    status: "In Progress",
    priority: "Low",
    assignee: "Asha Iyer",
    tags: ["design", "frontend"],
    dueDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString(),
    points: 5,
    comments: [],
    sprintId: null,
  },
  {
    id: "T-1009",
    title: "QA: Parallel socket address fix",
    description: "Refactor HTTP client factory to avoid port collisions.",
    status: "Review",
    priority: "High",
    assignee: "Neha Verma",
    tags: ["qa", "net"],
    dueDate: null,
    points: 5,
    comments: [],
    sprintId: null,
  },
  {
    id: "T-1010",
    title: "DB: Add composite index for search",
    description: "Speed up search by adding (tenant_id, status, created_at).",
    status: "Done",
    priority: "High",
    assignee: "Rohit Kumar",
    tags: ["db", "performance"],
    dueDate: null,
    points: 3,
    comments: [],
    sprintId: null,
  },
  {
    id: "T-1011",
    title: "Security: CSP + HSTS headers",
    description: "Lock down content sources and enforce HSTS preload.",
    status: "To Do",
    priority: "High",
    assignee: "Amit Shah",
    tags: ["security", "headers"],
    dueDate: null,
    points: 5,
    comments: [],
    sprintId: null,
  },
  {
    id: "T-1012",
    title: "UX: Empty state illustrations",
    description: "Add friendly empty states to dashboard widgets.",
    status: "Backlog",
    priority: "Low",
    assignee: "Asha Iyer",
    tags: ["ux", "illustration"],
    dueDate: null,
    points: 2,
    comments: [],
    sprintId: null,
  },
];

// ---------- Utils ----------
function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : initialValue;
    } catch {
      return initialValue;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {}
  }, [key, value]);
  return [value, setValue];
}

function initials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function colorFromName(name: string): string {
  const colors = [
    "bg-sky-500",
    "bg-rose-500",
    "bg-violet-500",
    "bg-emerald-500",
    "bg-amber-500",
    "bg-indigo-500",
    "bg-fuchsia-500",
    "bg-teal-500",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const idx = Math.abs(hash) % colors.length;
  return colors[idx];
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function classNames(...xs: Array<string | false | null | undefined>): string {
  return xs.filter(Boolean).join(" ");
}

// ---------- Components ----------
export default function JiraLikeBoard(): JSX.Element {
  const [tasks, setTasks] = useLocalStorage<Task[]>(STORAGE_KEY, SAMPLE_TASKS);
  const [sprints, setSprints] = useLocalStorage<Sprint[]>(SPRINTS_STORAGE_KEY, []);
  const [activeSprint, setActiveSprint] = useState<Sprint | null>(null);
  const [viewingSprint, setViewingSprint] = useState<Sprint | null>(null);

  useEffect(() => {
    const active = sprints.find(s => s.status === "Active") || null;
    setActiveSprint(active);
  }, [sprints]);
  const [query, setQuery] = useState<string>("");
  const [priorityFilter, setPriorityFilter] = useState<"All" | Priority>("All");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("All");
  const [showAdd, setShowAdd] = useState<boolean>(false);
  const [showSprintModal, setShowSprintModal] = useState<boolean>(false);
  const [viewTask, setViewTask] = useState<Task | null>(null);
  const [dragOver, setDragOver] = useState<Status | null>(null);
  const draggingIdRef = useRef<string | null>(null);

  const assignees = useMemo(
    () => Array.from(new Set(tasks.map((t) => t.assignee))),
    [tasks]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const currentSprint = viewingSprint || activeSprint;
    const sprintTasks = currentSprint 
      ? tasks.filter(t => t.sprintId === currentSprint.id || (t.status === "Backlog" && !viewingSprint))
      : tasks.filter(t => !t.sprintId || t.status === "Backlog");
    
    const result = sprintTasks.filter((t) => {
      if (priorityFilter !== "All" && t.priority !== priorityFilter) return false;
      if (assigneeFilter !== "All" && t.assignee !== assigneeFilter) return false;
      if (!q) return true;
      const hay = [t.title, t.description, t.assignee, ...t.tags].join(" ").toLowerCase();
      return hay.includes(q);
    });
    
    // Reset filters if no tasks match and filters are active
    if (result.length === 0 && sprintTasks.length > 0 && (priorityFilter !== "All" || assigneeFilter !== "All" || query.trim())) {
      setTimeout(() => {
        setPriorityFilter("All");
        setAssigneeFilter("All");
        setQuery("");
      }, 0);
    }
    
    return result;
  }, [tasks, query, priorityFilter, assigneeFilter, activeSprint, viewingSprint]);

  const grouped = useMemo(() => {
    const map: Record<Status, Task[]> = {
      Backlog: [],
      "To Do": [],
      "In Progress": [],
      Review: [],
      Done: [],
    };
    for (const t of filtered) map[t.status].push(t);
    return map;
  }, [filtered]);

  const onDragStart = (e: React.DragEvent<HTMLElement>, id: string) => {
    draggingIdRef.current = id;
    e.dataTransfer.setData("text/plain", id);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (e: React.DragEvent<HTMLElement>, status: Status) => {
    e.preventDefault();
    setDragOver(status);
    e.dataTransfer.dropEffect = "move";
  };

  const onDrop = (e: React.DragEvent<HTMLElement>, status: Status) => {
    e.preventDefault();
    const id = draggingIdRef.current || e.dataTransfer.getData("text/plain");
    if (!id) return;
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
    draggingIdRef.current = null;
    setDragOver(null);
  };

  const removeTask = (id: string) => setTasks((prev) => prev.filter((t) => t.id !== id));

  const addTask = (payload: Omit<Task, "id">) => {
    const id = `T-${Math.floor(1000 + Math.random() * 9000)}`;
    const newTask: Task = { id, ...payload };
    setTasks((prev) => [newTask, ...prev]);
  };

  const createSprint = (name: string) => {
    const id = `S-${Date.now()}`;
    const newSprint: Sprint = {
      id,
      name,
      startDate: null,
      endDate: null,
      status: "Planning"
    };
    setSprints(prev => [...prev, newSprint]);
    return newSprint;
  };

  const startSprint = (sprintId: string, endDate: string) => {
    setSprints(prev => prev.map(s => 
      s.id === sprintId 
        ? { ...s, status: "Active" as const, startDate: new Date().toISOString(), endDate }
        : s.status === "Active" ? { ...s, status: "Completed" as const } : s
    ));
    const sprint = sprints.find(s => s.id === sprintId);
    if (sprint) setActiveSprint({ ...sprint, status: "Active", startDate: new Date().toISOString(), endDate });
  };

  const endSprint = () => {
    if (!activeSprint) return;
    setSprints(prev => prev.map(s => 
      s.id === activeSprint.id ? { ...s, status: "Completed" as const } : s
    ));
    setActiveSprint(null);
  };

  const moveToSprint = (taskId: string, sprintId: string | null) => {
    setTasks(prev => prev.map(t => 
      t.id === taskId ? { ...t, sprintId, status: sprintId ? "To Do" as Status : "Backlog" as Status } : t
    ));
  };

  const addComment = (taskId: string, comment: Omit<Comment, "id">) => {
    const commentId = `C-${Date.now()}`;
    const newComment: Comment = { id: commentId, ...comment };
    setTasks((prev) => prev.map((t) => {
      if (t.id === taskId) {
        const updatedTask = { ...t, comments: [...(t.comments || []), newComment] };
        if (viewTask?.id === taskId) setViewTask(updatedTask);
        return updatedTask;
      }
      return t;
    }));
  };

  const changeAssignee = (taskId: string, newAssignee: string) => {
    setTasks((prev) => prev.map((t) => 
      t.id === taskId ? { ...t, assignee: newAssignee } : t
    ));
  };

  const updatePoints = (taskId: string, newPoints: number) => {
    setTasks((prev) => prev.map((t) => 
      t.id === taskId ? { ...t, points: newPoints } : t
    ));
  };

  const updateTask = (taskId: string, updates: Partial<Task>) => {
    setTasks((prev) => prev.map((t) => {
      if (t.id === taskId) {
        const updatedTask = { ...t, ...updates };
        if (viewTask?.id === taskId) setViewTask(updatedTask);
        return updatedTask;
      }
      return t;
    }));
  };

  const totalPoints = useMemo(() => tasks.reduce((s, t) => s + (t.points || 0), 0), [tasks]);

  return (
    <div className="w-full h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 text-slate-800">
      {/* Top Bar */}
      <header className="sticky top-0 z-30 border-b bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="shrink-0 h-9 w-9 rounded-xl bg-gradient-to-tr from-indigo-500 to-fuchsia-500" />
          <div>
            <h1 className="text-lg font-semibold leading-tight">
              {viewingSprint ? viewingSprint.name : activeSprint ? activeSprint.name : "Backlog"}
              {viewingSprint && (
                <button 
                  onClick={() => setViewingSprint(null)}
                  className="ml-2 text-sm text-indigo-600 hover:text-indigo-800"
                >
                  ← Back to current
                </button>
              )}
            </h1>
            <p className="text-xs text-slate-500">
              {viewingSprint ? `Viewing ${viewingSprint.status} Sprint` : activeSprint ? "Active Sprint" : "No Active Sprint"} · {filtered.length} tasks · {totalPoints} pts
              {(viewingSprint?.endDate || activeSprint?.endDate) && ` · Ends ${new Date((viewingSprint?.endDate || activeSprint?.endDate)!).toLocaleDateString()}`}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <input
              value={query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
              placeholder="Search tasks, tags, people..."
              className="w-64 md:w-80 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <select
              value={priorityFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPriorityFilter(e.target.value as Priority | "All")}
              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value="All">All priorities</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
            <select
              value={assigneeFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAssigneeFilter(e.target.value)}
              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value="All">All assignees</option>
              {assignees.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
            <button
              onClick={() => setShowSprintModal(true)}
              className="rounded-xl bg-green-600 text-white text-sm px-3 py-2 hover:bg-green-700 active:bg-green-800"
            >
              Sprint
            </button>
            <button
              onClick={() => setShowAdd(true)}
              className="rounded-xl bg-indigo-600 text-white text-sm px-3 py-2 hover:bg-indigo-700 active:bg-indigo-800"
            >
              + Add task
            </button>
          </div>
        </div>
      </header>

      {/* Columns */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="min-w-[1100px] grid grid-flow-col auto-cols-[minmax(260px,1fr)] gap-4 p-4 max-w-7xl mx-auto">
          {STATUSES.map((status) => (
            <Column
              key={status}
              status={status}
              tasks={grouped[status]}
              dragOver={dragOver === status}
              onDragOver={(e) => onDragOver(e, status)}
              onDrop={(e) => onDrop(e, status)}
              onDragStart={onDragStart}
              onRemove={removeTask}
              onView={setViewTask}
              onChangeAssignee={changeAssignee}
              assignees={assignees}
              onUpdatePoints={updatePoints}
            />
          ))}
        </div>
      </div>

      {/* Sprint Modal */}
      {showSprintModal && (
        <SprintModal
          onClose={() => setShowSprintModal(false)}
          sprints={sprints}
          activeSprint={activeSprint}
          onCreateSprint={createSprint}
          onStartSprint={startSprint}
          onEndSprint={endSprint}
          backlogTasks={tasks.filter(t => t.status === "Backlog" && !t.sprintId)}
          onMoveToSprint={moveToSprint}
          tasks={tasks}
          onViewSprint={setViewingSprint}
        />
      )}

      {/* Add Task Modal */}
      {showAdd && (
        <AddTaskModal
          onClose={() => setShowAdd(false)}
          onSubmit={(payload) => {
            addTask(payload);
            setShowAdd(false);
          }}
          assignees={assignees}
        />
      )}

      {/* View Task Modal */}
      {viewTask && (
        <ViewTaskModal
          task={viewTask}
          onClose={() => setViewTask(null)}
          onAddComment={addComment}
          onUpdateTask={updateTask}
          onRemove={(id) => {
            removeTask(id);
            setViewTask(null);
          }}
          assignees={assignees}
        />
      )}
    </div>
  );
}

// ---------- Subcomponents ----------
interface ColumnProps {
  status: Status;
  tasks: Task[];
  dragOver: boolean;
  onDragOver: (e: React.DragEvent<HTMLElement>) => void;
  onDrop: (e: React.DragEvent<HTMLElement>) => void;
  onDragStart: (e: React.DragEvent<HTMLElement>, id: string) => void;
  onRemove: (id: string) => void;
  onView: (task: Task) => void;
  onChangeAssignee: (taskId: string, assignee: string) => void;
  assignees: string[];
  onUpdatePoints: (taskId: string, points: number) => void;
}

function Column({ status, tasks, dragOver, onDragOver, onDrop, onDragStart, onRemove, onView, onChangeAssignee, assignees, onUpdatePoints }: ColumnProps) {
  const countHigh = tasks.filter((t) => t.priority === "High").length;
  const count = tasks.length;
  return (
    <section
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={classNames(
        "flex flex-col rounded-2xl border bg-white/70 backdrop-blur p-3 transition-shadow",
        dragOver ? "ring-2 ring-indigo-500 shadow-xl" : "shadow-sm"
      )}
      aria-label={`${status} column`}
    >
      <header className="flex items-center justify-between px-1 pb-2">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-slate-700">{status}</h2>
          <span className="text-xs text-slate-500">{count}</span>
        </div>
        {countHigh > 0 && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">{countHigh} High</span>
        )}
      </header>

      <div className="flex-1 overflow-y-auto max-h-[calc(100vh-200px)] min-h-[300px] space-y-3 pt-1" role="list">
        {tasks.map((t) => (
          <Card key={t.id} task={t} onDragStart={onDragStart} onRemove={onRemove} onView={onView} onChangeAssignee={onChangeAssignee} assignees={assignees} onUpdatePoints={onUpdatePoints} />
        ))}
      </div>
    </section>
  );
}

interface CardProps {
  task: Task;
  onDragStart: (e: React.DragEvent<HTMLElement>, id: string) => void;
  onRemove: (id: string) => void;
  onView: (task: Task) => void;
  onChangeAssignee: (taskId: string, assignee: string) => void;
  assignees: string[];
  onUpdatePoints: (taskId: string, points: number) => void;
}

function Card({ task, onDragStart, onRemove, onView, onChangeAssignee, assignees, onUpdatePoints }: CardProps) {
  const { id, title, description, priority, assignee, tags, dueDate, points } = task;
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [editingPoints, setEditingPoints] = useState(false);
  const [pointsValue, setPointsValue] = useState(points.toString());
  const priorityClass = PRIORITY_COLORS[priority];
  return (
    <article
      draggable
      onDragStart={(e) => onDragStart(e, id)}
      className="group cursor-grab active:cursor-grabbing rounded-xl border border-slate-200 bg-white p-3 shadow hover:shadow-md transition-shadow"
      role="listitem"
    >
      <div className="flex items-start gap-2">
        <span className={classNames("inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium", priorityClass)}>
          {priority}
        </span>
        <span className="ml-auto text-[10px] text-slate-400">{id}</span>
      </div>
      <h3 
        className="mt-2 text-sm font-semibold text-slate-800 leading-snug cursor-pointer hover:text-indigo-600"
        onClick={() => onView(task)}
      >
        {title}
      </h3>
      {description && (
        <p className="mt-1 text-xs text-slate-600 line-clamp-2">{description}</p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {tags.map((tg) => (
          <span key={tg} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">#{tg}</span>
        ))}
        {typeof points === "number" && (
          <div className="ml-auto">
            {editingPoints ? (
              <input
                type="number"
                min={0}
                value={pointsValue}
                onChange={(e) => setPointsValue(e.target.value)}
                onBlur={() => {
                  const newPoints = Number(pointsValue) || 0;
                  onUpdatePoints(id, newPoints);
                  setEditingPoints(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const newPoints = Number(pointsValue) || 0;
                    onUpdatePoints(id, newPoints);
                    setEditingPoints(false);
                  }
                  if (e.key === 'Escape') {
                    setPointsValue(points.toString());
                    setEditingPoints(false);
                  }
                }}
                className="w-12 text-[11px] px-1 py-0.5 rounded border border-indigo-300 text-center"
                autoFocus
              />
            ) : (
              <span 
                className="text-[11px] px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 cursor-pointer hover:bg-indigo-100"
                onClick={() => setEditingPoints(true)}
              >
                {points} pts
              </span>
            )}
          </div>
        )}
      </div>
      <div className="mt-3 flex items-center justify-between">
        <div className="relative">
          <div 
            className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded px-1 py-1"
            onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
          >
            <Avatar name={assignee} />
            <span className="text-xs text-slate-700">{assignee}</span>
          </div>
          {showAssigneeDropdown && (
            <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg z-10 min-w-32">
              {assignees.map((person) => (
                <button
                  key={person}
                  onClick={() => {
                    onChangeAssignee(id, person);
                    setShowAssigneeDropdown(false);
                  }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2"
                >
                  <Avatar name={person} />
                  {person}
                </button>
              ))}
            </div>
          )}
        </div>
        {dueDate ? (
          <span
            className={classNames(
              "text-[11px] px-2 py-0.5 rounded border",
              new Date(dueDate) < new Date()
                ? "bg-rose-50 text-rose-700 border-rose-200"
                : "bg-emerald-50 text-emerald-700 border-emerald-200"
            )}
            title={new Date(dueDate).toLocaleString()}
          >
            Due {formatDate(dueDate)}
          </span>
        ) : (
          <span className="text-[11px] text-slate-400">No due</span>
        )}
      </div>

    </article>
  );
}

function Avatar({ name }: { name: string }) {
  const bg = colorFromName(name);
  return (
    <div className={classNames("h-6 w-6 rounded-full grid place-items-center text-[10px] font-semibold text-white", bg)} title={name}>
      {initials(name)}
    </div>
  );
}

interface ViewTaskModalProps {
  task: Task;
  onClose: () => void;
  onAddComment: (taskId: string, comment: Omit<Comment, "id">) => void;
  onUpdateTask: (taskId: string, updates: Partial<Task>) => void;
  onRemove: (id: string) => void;
  assignees: string[];
}

function ViewTaskModal({ task, onClose, onAddComment, onUpdateTask, onRemove, assignees }: ViewTaskModalProps) {
  const { id, title, description, status, priority, assignee, tags, dueDate, points, comments = [] } = task;
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(title);
  const [editDescription, setEditDescription] = useState(description);
  const [editStatus, setEditStatus] = useState(status);
  const [editPriority, setEditPriority] = useState(priority);
  const [editAssignee, setEditAssignee] = useState(assignee);
  const [editPoints, setEditPoints] = useState(points.toString());
  const [editDueDate, setEditDueDate] = useState(dueDate ? dueDate.split('T')[0] : '');
  const [editTags, setEditTags] = useState(tags.join(', '));
  const priorityClass = PRIORITY_COLORS[priority];

  const saveChanges = () => {
    onUpdateTask(id, {
      title: editTitle.trim(),
      description: editDescription.trim(),
      status: editStatus,
      priority: editPriority,
      assignee: editAssignee,
      points: Number(editPoints) || 0,
      dueDate: editDueDate ? new Date(editDueDate).toISOString() : null,
      tags: editTags.split(',').map(t => t.trim()).filter(Boolean)
    });
    setIsEditing(false);
  };

  const cancelEdit = () => {
    setEditTitle(title);
    setEditDescription(description);
    setEditStatus(status);
    setEditPriority(priority);
    setEditAssignee(assignee);
    setEditPoints(points.toString());
    setEditDueDate(dueDate ? dueDate.split('T')[0] : '');
    setEditTags(tags.join(', '));
    setIsEditing(false);
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-xl border p-6">
        <div className="flex items-center justify-between pb-4 border-b">
          <div className="flex items-center gap-3">
            {isEditing ? (
              <input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="text-xl font-semibold bg-transparent border-b border-slate-300 focus:border-indigo-500 outline-none"
              />
            ) : (
              <h3 className="text-xl font-semibold">{title}</h3>
            )}
            <span className="text-sm text-slate-500">{id}</span>
          </div>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button onClick={saveChanges} className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700">Save</button>
                <button onClick={cancelEdit} className="px-3 py-1 text-sm border rounded hover:bg-slate-50">Cancel</button>
              </>
            ) : (
              <button onClick={() => setIsEditing(true)} className="px-3 py-1 text-sm border rounded hover:bg-slate-50">Edit</button>
            )}
            <button onClick={onClose} className="text-slate-500 hover:text-slate-700 text-xl">✕</button>
          </div>
        </div>

        <div className="mt-6 space-y-6">
          <div className="flex items-center gap-4">
            {isEditing ? (
              <>
                <select value={editPriority} onChange={(e) => setEditPriority(e.target.value as Priority)} className="px-3 py-1 text-sm border rounded">
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
                <select value={editStatus} onChange={(e) => setEditStatus(e.target.value as Status)} className="px-3 py-1 text-sm border rounded">
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <input type="number" min={0} value={editPoints} onChange={(e) => setEditPoints(e.target.value)} className="w-16 px-2 py-1 text-sm border rounded" placeholder="pts" />
              </>
            ) : (
              <>
                <span className={classNames("inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium", priorityClass)}>
                  {priority}
                </span>
                <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm">{status}</span>
                {points && <span className="px-3 py-1 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 text-sm">{points} pts</span>}
              </>
            )}
          </div>

          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-2">Description</h4>
            {isEditing ? (
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="w-full p-3 border rounded-lg text-sm"
                rows={3}
                placeholder="Task description..."
              />
            ) : (
              <p className="text-slate-600">{description || 'No description'}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Assignee</h4>
              {isEditing ? (
                <select value={editAssignee} onChange={(e) => setEditAssignee(e.target.value)} className="w-full px-3 py-2 border rounded">
                  {assignees.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              ) : (
                <div className="flex items-center gap-2">
                  <Avatar name={assignee} />
                  <span className="text-slate-700">{assignee}</span>
                </div>
              )}
            </div>

            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Due Date</h4>
              {isEditing ? (
                <input
                  type="date"
                  value={editDueDate}
                  onChange={(e) => setEditDueDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                />
              ) : dueDate ? (
                <span
                  className={classNames(
                    "text-sm px-3 py-1 rounded border",
                    new Date(dueDate) < new Date()
                      ? "bg-rose-50 text-rose-700 border-rose-200"
                      : "bg-emerald-50 text-emerald-700 border-emerald-200"
                  )}
                >
                  {formatDate(dueDate)}
                </span>
              ) : (
                <span className="text-slate-500">No due date</span>
              )}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-2">Tags</h4>
            {isEditing ? (
              <input
                value={editTags}
                onChange={(e) => setEditTags(e.target.value)}
                className="w-full px-3 py-2 border rounded"
                placeholder="Comma-separated tags (e.g., frontend, bug, urgent)"
              />
            ) : tags.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span key={tag} className="text-sm px-3 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">#{tag}</span>
                ))}
              </div>
            ) : (
              <span className="text-slate-500">No tags</span>
            )}
          </div>

          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-3">Comments ({comments?.length || 0})</h4>
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {comments?.map((comment) => (
                <div key={comment.id} className="border rounded-lg p-3 bg-slate-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Avatar name={comment.author} />
                    <span className="text-sm font-medium">{comment.author}</span>
                    <span className="text-xs text-slate-500">{new Date(comment.timestamp).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-slate-700">{comment.text}</p>
                  {comment.image && (
                    <img src={comment.image} alt="Comment attachment" className="mt-2 max-w-xs rounded border" />
                  )}
                </div>
              ))}
            </div>
            <CommentForm onSubmit={(comment) => onAddComment(id, comment)} />
          </div>
        </div>

        <div className="mt-6 flex justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => navigator.clipboard?.writeText(id)}
              className="px-3 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50"
            >
              Copy ID
            </button>
            <button
              onClick={() => onRemove(id)}
              className="px-3 py-2 text-sm rounded-lg border border-rose-300 text-rose-600 hover:bg-rose-50"
            >
              Delete Task
            </button>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg bg-slate-100 hover:bg-slate-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

interface AddTaskModalProps {
  onClose: () => void;
  onSubmit: (payload: Omit<Task, "id">) => void;
  assignees: string[];
}

interface CommentFormProps {
  onSubmit: (comment: Omit<Comment, "id">) => void;
}

function CommentForm({ onSubmit }: CommentFormProps) {
  const [text, setText] = useState("");
  const [image, setImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setImage(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const submit = () => {
    if (!text.trim()) return;
    onSubmit({
      text: text.trim(),
      author: "Current User",
      timestamp: new Date().toISOString(),
      image: image || undefined,
    });
    setText("");
    setImage(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="mt-4 border-t pt-4">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Add a comment..."
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
        rows={3}
      />
      {image && (
        <div className="mt-2 relative inline-block">
          <img src={image} alt="Upload preview" className="max-w-xs rounded border" />
          <button
            onClick={() => setImage(null)}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
          >
            ×
          </button>
        </div>
      )}
      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="text-sm px-3 py-1 rounded border border-slate-300 hover:bg-slate-50"
          >
            📎 Attach Image
          </button>
        </div>
        <button
          onClick={submit}
          disabled={!text.trim()}
          className="px-3 py-1 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          Comment
        </button>
      </div>
    </div>
  );
}

interface SprintModalProps {
  onClose: () => void;
  sprints: Sprint[];
  activeSprint: Sprint | null;
  onCreateSprint: (name: string) => Sprint;
  onStartSprint: (sprintId: string, endDate: string) => void;
  onEndSprint: () => void;
  backlogTasks: Task[];
  onMoveToSprint: (taskId: string, sprintId: string | null) => void;
  tasks: Task[];
  onViewSprint: (sprint: Sprint) => void;
}

function SprintModal({ onClose, sprints, activeSprint, onCreateSprint, onStartSprint, onEndSprint, backlogTasks, onMoveToSprint, tasks, onViewSprint }: SprintModalProps) {
  const [activeTab, setActiveTab] = useState<"current" | "manage">("current");
  const [sprintName, setSprintName] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);

  const handleCreateAndStart = () => {
    if (!sprintName.trim() || !endDate) return;
    const sprint = onCreateSprint(sprintName.trim());
    selectedTasks.forEach(taskId => onMoveToSprint(taskId, sprint.id));
    onStartSprint(sprint.id, new Date(endDate).toISOString());
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-xl border p-6">
        <div className="flex items-center justify-between pb-4 border-b">
          <h3 className="text-xl font-semibold">Sprint Management</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 text-xl">✕</button>
        </div>

        <div className="flex border-b mt-4">
          <button
            onClick={() => setActiveTab("current")}
            className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === "current" ? "border-indigo-500 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            Current Sprint
          </button>
          <button
            onClick={() => setActiveTab("manage")}
            className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === "manage" ? "border-indigo-500 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            All Sprints
          </button>
        </div>

        <div className="mt-6 space-y-6">
          {activeTab === "current" ? (
            activeSprint ? (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-green-800">{activeSprint.name}</h4>
                    <p className="text-sm text-green-600">Active Sprint</p>
                    {activeSprint.endDate && (
                      <p className="text-xs text-green-500">Ends: {new Date(activeSprint.endDate).toLocaleDateString()}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { onEndSprint(); onClose(); }}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      End Sprint
                    </button>
                    <button
                      onClick={() => { onEndSprint(); setActiveTab("current"); }}
                      className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      End & Start New
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Sprint Name</label>
                  <input
                    value={sprintName}
                    onChange={(e) => setSprintName(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Sprint 1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Select Backlog Tasks</label>
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {backlogTasks.map(task => (
                      <label key={task.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded">
                        <input
                          type="checkbox"
                          checked={selectedTasks.includes(task.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTasks(prev => [...prev, task.id]);
                            } else {
                              setSelectedTasks(prev => prev.filter(id => id !== task.id));
                            }
                          }}
                        />
                        <span className="text-sm">{task.title}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <button
                  onClick={handleCreateAndStart}
                  disabled={!sprintName.trim() || !endDate}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  Create & Start Sprint
                </button>
              </div>
            )
          ) : (
            <div className="space-y-4">
              <h4 className="font-medium text-slate-700">Sprint Management</h4>
              <div className="max-h-96 overflow-y-auto space-y-3">
                {sprints.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No sprints created yet</p>
                ) : (
                  sprints.map(sprint => {
                    const sprintTasks = tasks.filter(t => t.sprintId === sprint.id);
                    const completedTasks = sprintTasks.filter(t => t.status === "Done");
                    const statusColors = {
                      Planning: "bg-yellow-50 border-yellow-200 text-yellow-800",
                      Active: "bg-green-50 border-green-200 text-green-800",
                      Completed: "bg-slate-50 border-slate-200 text-slate-600"
                    };
                    return (
                      <div key={sprint.id} className={`p-3 rounded-lg border ${statusColors[sprint.status]} cursor-pointer hover:shadow-md transition-shadow`} onClick={() => { onViewSprint(sprint); onClose(); }}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h5 className="font-medium">{sprint.name}</h5>
                            <p className="text-sm opacity-75">{sprint.status}</p>
                            {sprint.startDate && (
                              <p className="text-xs opacity-60">
                                Started: {new Date(sprint.startDate).toLocaleDateString()}
                                {sprint.endDate && ` - Ends: ${new Date(sprint.endDate).toLocaleDateString()}`}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="text-xs px-2 py-1 rounded bg-white/50">{sprint.id}</span>
                            <p className="text-xs mt-1">{sprintTasks.length} tasks</p>
                            {sprint.status === "Completed" && (
                              <p className="text-xs text-green-600">{completedTasks.length} completed</p>
                            )}
                          </div>
                        </div>
                        {sprintTasks.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-white/50">
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {sprintTasks.map(task => (
                                <div key={task.id} className="flex items-center justify-between text-xs">
                                  <span className={task.status === "Done" ? "line-through opacity-60" : ""}>
                                    {task.title}
                                  </span>
                                  <span className={`px-1 py-0.5 rounded text-[10px] ${
                                    task.status === "Done" ? "bg-green-100 text-green-700" :
                                    task.status === "In Progress" ? "bg-blue-100 text-blue-700" :
                                    "bg-slate-100 text-slate-600"
                                  }`}>
                                    {task.status}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AddTaskModal({ onClose, onSubmit, assignees }: AddTaskModalProps) {
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [status, setStatus] = useState<Status>("To Do");
  const [priority, setPriority] = useState<Priority>("Medium");
  const [assignee, setAssignee] = useState<string>("Unassigned");
  const [tags, setTags] = useState<string>("");
  const [points, setPoints] = useState<number>(3);
  const [dueDate, setDueDate] = useState<string>("");

  const submit = () => {
    if (!title.trim()) return;
    onSubmit({
      title: title.trim(),
      description: description.trim(),
      status,
      priority,
      assignee,
      tags: tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      dueDate: dueDate ? new Date(dueDate).toISOString() : null,
      points: Number(points) || 0,
      comments: [],
      sprintId: null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-xl border p-4">
        <div className="flex items-center justify-between pb-2 border-b">
          <h3 className="text-base font-semibold">Add Task</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700">✕</button>
        </div>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Title</span>
            <input
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., Implement feature flags"
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="block text-slate-600 mb-1">Description</span>
            <textarea
              value={description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm h-20 focus:ring-2 focus:ring-indigo-500"
              placeholder="Optional details for the task"
            />
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Status</span>
            <select
              value={status}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setStatus(e.target.value as Status)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Priority</span>
            <select
              value={priority}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPriority(e.target.value as Priority)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option>High</option>
              <option>Medium</option>
              <option>Low</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Assignee</span>
            <select
              value={assignee}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAssignee(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value="Unassigned">Unassigned</option>
              {assignees.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Story Points</span>
            <input
              type="number"
              min={0}
              value={points}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPoints(Number(e.target.value))}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Due Date</span>
            <input
              type="date"
              value={dueDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDueDate(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="block text-slate-600 mb-1">Tags</span>
            <input
              value={tags}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTags(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
              placeholder="Comma-separated (e.g., auth, backend)"
            />
          </label>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm rounded-lg border border-slate-300">Cancel</button>
          <button
            onClick={submit}
            className="px-3 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            disabled={!title.trim()}
          >
            Create task
          </button>
        </div>
      </div>
    </div>
  );
}
